import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Lead, Category } from '../../types';
import Input from '../ui/Input';
import { Search, X, Mail, Plus } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useLeadOptions } from '../../utils/leadOptions';
import ContactIconWithCategory from '../ui/ContactIconWithCategory';
import SectorIcon from '../ui/SectorIcon';
import LeadStatusBadge from '../leads/LeadStatusBadge';
import type { RequestCreateLeadArgs } from '../../hooks/useCreateLeadFromSearch';
import { useSearchCreateFocus } from '../../hooks/useSearchCreateFocus';
import { useFollowTriggerPosition } from '../../hooks/useFollowTriggerPosition';

interface LeadSearchProps {
  leads: Lead[];
  selectedLead: Lead | null;
  onSelectLead: (lead: Lead | null) => void;
  categories?: Category[];
  placeholder?: string;
  error?: string;
  searchFn?: (term: string) => Promise<Lead[]>;
  /** Sticky "Créer un lead" footer — typically useCreateLeadFromSearch().requestCreateLead */
  onCreateLead?: (args: RequestCreateLeadArgs) => void;
}

const LeadSearch: React.FC<LeadSearchProps> = ({
  leads,
  selectedLead,
  onSelectLead,
  categories = [],
  placeholder,
  error,
  searchFn,
  onCreateLead,
}) => {
  const { t } = useI18n();
  const { getSectorLabel, getSectorById } = useLeadOptions();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [remoteLeads, setRemoteLeads] = useState<Lead[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchSeqRef = useRef(0);
  const selectedLeadRef = useRef(selectedLead);
  selectedLeadRef.current = selectedLead;
  const { inputRef, shouldOpenOnFocus, commitSelectionUi } = useSearchCreateFocus();
  const menuRef = useRef<HTMLDivElement>(null);

  const computeMenuPosition = useCallback((rect: DOMRect) => ({
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  }), []);

  const menuPosition = useFollowTriggerPosition({
    open: isOpen,
    triggerRef: inputRef,
    menuRef,
    computePosition: computeMenuPosition,
  });

  useEffect(() => {
    setSearchTerm(selectedLead ? selectedLead.name : '');
  }, [selectedLead]);

  const filteredLeads = useMemo(() => {
    const q = searchTerm.trim();
    if (searchFn) return remoteLeads;
    if (!q) return leads.slice(0, 10);
    return leads
      .filter(
        (lead) =>
          lead.name?.toLowerCase().includes(q.toLowerCase()) ||
          lead.contact_value?.toLowerCase().includes(q.toLowerCase()) ||
          getSectorLabel(lead.sector_id).toLowerCase().includes(q.toLowerCase())
      )
      .slice(0, 10);
  }, [leads, searchTerm, getSectorLabel, searchFn, remoteLeads]);

  useEffect(() => {
    if (!searchFn || !isOpen) return;

    const seq = ++searchSeqRef.current;
    const q = searchTerm.trim();
    setIsSearching(true);

    const timeout = setTimeout(() => {
      searchFn(q)
        .then((results) => {
          if (seq !== searchSeqRef.current) return;
          setRemoteLeads(results.slice(0, 10));
          setIsSearching(false);
        })
        .catch(() => {
          if (seq !== searchSeqRef.current) return;
          setRemoteLeads([]);
          setIsSearching(false);
        });
    }, q ? 200 : 0);

    return () => clearTimeout(timeout);
  }, [searchFn, searchTerm, isOpen]);

  const handleSelect = (lead: Lead) => {
    setSearchTerm(lead.name);
    setIsOpen(false);
    onSelectLead(lead);
  };

  const handleClear = () => {
    setSearchTerm('');
    setIsOpen(false);
    onSelectLead(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    const current = selectedLeadRef.current;
    const committed = !!(current && searchTerm === current.name);
    if (shouldOpenOnFocus(committed)) setIsOpen(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      const current = selectedLeadRef.current;
      if (current) {
        setSearchTerm((term) => (term !== current.name ? current.name : term));
      }
    }, 200);
  };

  const handleCreateLead = () => {
    if (!onCreateLead) return;
    const prefillName =
      !selectedLead || searchTerm.trim() !== selectedLead.name ? searchTerm.trim() : '';
    setIsOpen(false);
    onCreateLead({
      prefillName,
      onCreated: (lead) => {
        setSearchTerm(lead.name);
        setIsOpen(false);
        onSelectLead(lead);
        commitSelectionUi();
      },
    });
  };

  const showCreate = typeof onCreateLead === 'function';
  const namedPrefill =
    searchTerm.trim() && (!selectedLead || searchTerm.trim() !== selectedLead.name)
      ? searchTerm.trim()
      : '';

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          icon={<Search className="w-4 h-4" />}
          error={error}
          className="focus:ring-inset"
        />
        {selectedLead && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen &&
        menuPosition &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              zIndex: 9999,
            }}
            className="bg-white border border-gray-200 rounded-xl shadow-paper overflow-hidden flex flex-col max-h-72"
          >
            <div className="overflow-y-auto overflow-x-hidden min-h-0 flex-1">
              {searchFn && isSearching ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                  {t('common.loading')}
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  {t('leads.noLeadsFound')}
                </div>
              ) : (
                filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(lead)}
                    className="group px-4 py-3 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <SectorIcon
                        category={getSectorById(lead.sector_id)}
                        showTooltip
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-primary-700 transition-colors">{lead.name}</p>
                          <LeadStatusBadge status={lead.status} className="shrink-0 max-w-[9rem] truncate" />
                        </div>
                        {lead.contact_value && (
                          <div className="flex items-center gap-1.5 min-w-0 text-xs text-gray-500 mt-1">
                            {lead.contact_type_id ? (
                              <ContactIconWithCategory
                                category={categories.find((c: Category) => c.id === lead.contact_type_id)}
                                value={lead.contact_value}
                                size="sm"
                                showTooltip={false}
                                className="text-gray-500"
                              />
                            ) : (
                              <Mail className="w-3 h-3 shrink-0" />
                            )}
                            <span className="truncate min-w-0">{lead.contact_value}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {showCreate && (
              <div className="shrink-0 border-t border-gray-200 bg-white">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCreateLead}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-50 transition-colors"
                >
                  <Plus className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                  <span className="truncate">
                    {namedPrefill
                      ? t('leads.createNamed', { name: namedPrefill })
                      : t('leads.createNew')}
                  </span>
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default LeadSearch;
